import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, NEVER, Observable, ReplaySubject, Subject, combineLatest, of } from 'rxjs';
import {
    catchError,
    distinctUntilChanged,
    filter,
    map,
    skip,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import {
    ActionItem,
    ActionItemStyle,
    BaseContainer,
    DottedKey,
    FormSelectOption,
    FormValue,
    FormValueState,
    LoadingBehaviorSubject,
    MultiFormSelectOptions,
    ThesaurusConceptReference
} from '@stylehaven/components';
import { isEqual } from 'lodash-es';
import { ThesaurusService } from '../../../common/services/thesaurus/thesaurus.service';

@Component({
    selector: 'med-thesaurus-search',
    templateUrl: './thesaurus-search.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ThesaurusSearchComponent extends BaseContainer implements OnInit, OnDestroy {
    public lookupLoading$: LoadingBehaviorSubject = new LoadingBehaviorSubject();
    public openDropdown$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public search$: BehaviorSubject<string> = new BehaviorSubject('');

    public disabled$: Observable<boolean>;
    public selectLabel$: Observable<string>;
    public title$: Observable<string>;

    public thesaurusOptions$: Subject<MultiFormSelectOptions<ThesaurusConceptReference>> = new Subject<
        MultiFormSelectOptions<ThesaurusConceptReference>
    >();

    readonly initialSelectedValues$: ReplaySubject<FormValue<ThesaurusConceptReference>[]> = new ReplaySubject<
        FormValue<ThesaurusConceptReference>[]
    >();

    readonly state$: ReplaySubject<FormValue<FormSelectOption<ThesaurusConceptReference>>[]> = new ReplaySubject<
        FormValue<FormSelectOption<ThesaurusConceptReference>>[]
    >(1);

    public noResultsActionItem$: Observable<ActionItem | null>;

    public itemCapCount = 25;

    private thesaurusDottedKey: DottedKey;

    @Input() thesaurusSearchControl: UntypedFormControl = new UntypedFormControl();
    @Input() set setValue(overrideValue: ThesaurusConceptReference | null) {
        this.manualOverride(overrideValue);
    }

    @Input() enableSuggestions = false;
    @Input() suggestionLimitReached = false;
    @Input() topConcept?: ThesaurusConceptReference;
    @Input() multiple = false;
    @Input() indeterminateMsg: string;
    @Input() disabledMsg?: string;
    @Input() autoFocus: boolean = false;

    @Input() set thesaurusDefDottedKey(thesaurusDefDottedKey: DottedKey) {
        if (!thesaurusDefDottedKey) {
            return;
        }

        this.initThesaurusResults(thesaurusDefDottedKey);
    }

    @Output()
    selectedConcept: EventEmitter<ThesaurusConceptReference | null> =
        new EventEmitter<ThesaurusConceptReference | null>();

    @Output() suggestion: EventEmitter<string> = new EventEmitter<string>();

    constructor(private thesaurusService: ThesaurusService) {
        super();
    }

    ngOnInit() {
        this.initLabelStreams();
        this.initNoResultsActionItem();

        this.initControlListener();
        this.initStatePropagator();
    }

    ngOnDestroy() {
        super.ngOnDestroy();

        this.lookupLoading$.complete();
        this.openDropdown$.complete();
        this.search$.complete();
        this.state$.complete();
        this.initialSelectedValues$.complete();
    }

    // #region LabelHelper
    // MED-4001 + MED-3618: Function provided to ShDroppanel to allow for translation of selected values unavailable in provided options
    // This way options can be lazy-loaded when opening dropdown
    get thesaurusConceptToLabel(): (concept: ThesaurusConceptReference) => string {
        return (concept: ThesaurusConceptReference) => this.thesaurusService.getThesaurusConceptReferenceLabel(concept);
    }
    // #endregion

    // #region Labels
    private initLabelStreams(): void {
        this.selectLabel$ = combineLatest([this.state$]) // Re-determine label if options have changed
            .pipe(map(([state]) => this.determineSelectLabel(state)));

        this.title$ = this.disabledMsg ? of(this.disabledMsg) : this.selectLabel$;
    }

    private determineSelectLabel(formValues: FormValue<FormSelectOption<ThesaurusConceptReference>>[]): string {
        const hasIndeterminate = !!formValues.find(formValue => formValue.state === FormValueState.INDETERMINATE);

        const setFormValues: FormValue<FormSelectOption<ThesaurusConceptReference>>[] = formValues.filter(
            formValue => formValue.state === FormValueState.SET
        );
        const count: number = setFormValues.length;

        let label = '';

        if (this.multiple) {
            return label;
        }

        if (hasIndeterminate) {
            label = `${this.indeterminateMsg}`;
        } else if (count === 1) {
            label = setFormValues[0].value.label;
        }

        return label;
    }

    // #endregion

    // #region Control listener
    private initControlListener(): void {
        this.thesaurusSearchControl.valueChanges
            .pipe(take(1), takeUntil(this.destroy$))
            .subscribe(values => this.setInitialValues(values));

        this.disabled$ = this.thesaurusSearchControl.statusChanges.pipe(
            startWith(this.thesaurusSearchControl.status),
            map(status => status === 'DISABLED'),
            takeUntil(this.destroy$)
        );
    }

    private setInitialValues(formValues: FormValue<ThesaurusConceptReference>[]): void {
        /*  superclass ensures that it's an FormValue<any>[],
            here we need to check that all values are of same type */

        const values: ThesaurusConceptReference[] = formValues.map(fv => fv.value);

        const allHaveSameType: boolean = formValues.every(fv => typeof fv.value === typeof formValues[0].value);

        if (!allHaveSameType) {
            console.error('ShThesaurusSearch:: all values must be of same type', values);
            this.initialSelectedValues$.next([]);
            return;
        }

        this.initialSelectedValues$.next(formValues as FormValue<ThesaurusConceptReference>[]);
    }

    // Used to manually override the value from the thesaurus tree
    private manualOverride(overrideValue: ThesaurusConceptReference | null): void {
        this.thesaurusSearchControl.setValue(overrideValue ? [new FormValue(overrideValue)] : []);
        this.initialSelectedValues$.next(overrideValue ? [new FormValue(overrideValue)] : []);
    }
    // #endregion

    // #region Propagate values
    private initStatePropagator(): void {
        this.state$
            .pipe(
                skip(1),
                map(state => this.stateToThesaurusConceptReference(state)),
                distinctUntilChanged(isEqual)
            )
            .subscribe(stateToPropagate => {
                this.onConceptSelection(stateToPropagate);
            });
    }

    private stateToThesaurusConceptReference(
        formSelectOptionsValues: FormValue<FormSelectOption<ThesaurusConceptReference>>[]
    ): ThesaurusConceptReference | null {
        const formValues = formSelectOptionsValues.map(
            formValueOfFormSelectOption =>
                new FormValue(formValueOfFormSelectOption.value.value, formValueOfFormSelectOption.state)
        );

        const hasIndeterminate: boolean =
            formValues.filter(value => value.state === FormValueState.INDETERMINATE).length >= 1;

        if (hasIndeterminate) {
            return null;
        }

        const selected: ThesaurusConceptReference | null =
            formValues.filter(value => value.state === FormValueState.SET)[0]?.value ?? null;

        return selected;
    }

    private onConceptSelection(selectedConcept: ThesaurusConceptReference | null) {
        this.selectedConcept.next(selectedConcept);

        if (this.multiple) {
            this.thesaurusSearchControl.setValue([]);
            this.search$.next('');
        }
    }
    // #endregion

    // #region Thesaurus results
    private initThesaurusResults(thesaurusDefDottedKey: DottedKey): void {
        this.thesaurusDottedKey = thesaurusDefDottedKey;

        const searchTrigger: Observable<string> = combineLatest([this.search$, this.openDropdown$]).pipe(
            filter(([searchTerm, openDropdown]) => openDropdown),
            map(([searchTerm, openDropdown]) => searchTerm),
            distinctUntilChanged(isEqual)
        );

        searchTrigger
            .pipe(
                tap(() => this.lookupLoading$.startLoading()),
                switchMap(searchTerm =>
                    this.thesaurusService
                        .getThesaurusFieldResults(this.thesaurusDottedKey, searchTerm, this.itemCapCount + 5)
                        .pipe(map(results => results))
                ),
                withLatestFrom(this.search$),
                map(([concepts, searchTerm]) => this.addTopConcept(concepts, searchTerm)),
                withLatestFrom(this.search$),
                map(([concepts, searchTerm]) =>
                    this.thesaurusConceptsToFormSelectOptions(
                        concepts,
                        searchTerm,
                        this.thesaurusSearchControl.value ?? []
                    )
                ),
                tap(() => this.lookupLoading$.stopLoading()),
                catchError(() => {
                    this.lookupLoading$.stopLoading();
                    return NEVER;
                }),
                takeUntil(this.destroy$)
            )
            .subscribe(options => {
                this.thesaurusOptions$.next(options);
            });
    }

    private addTopConcept(concepts: ThesaurusConceptReference[], searchTerm: string): ThesaurusConceptReference[] {
        if (!this.topConcept) {
            return concepts;
        }

        const topConceptLabel: string = this.thesaurusService.getThesaurusConceptReferenceLabel(this.topConcept);

        const matchesSearch: boolean = topConceptLabel.toLowerCase().startsWith(searchTerm.toLowerCase());

        return matchesSearch ? [...concepts, this.topConcept] : concepts;
    }

    private thesaurusConceptsToFormSelectOptions(
        searchResultConcepts: ThesaurusConceptReference[],
        searchTerm: string,
        controlValues: FormValue<ThesaurusConceptReference>[]
    ): MultiFormSelectOptions<ThesaurusConceptReference> {
        //MED-1731: Add selected values that match the search, but are not displayed due to results-limit
        const matchedMissingSelectedValues: FormValue<ThesaurusConceptReference>[] = controlValues
            .filter(formValue => formValue.state === FormValueState.SET)
            .filter((formValue: FormValue<ThesaurusConceptReference>) => {
                const matchesSearch: boolean = this.thesaurusService
                    .getThesaurusConceptReferenceLabel(formValue.value)
                    .toLowerCase()
                    .startsWith(searchTerm.toLowerCase());
                const notInResults = !searchResultConcepts.find(result => result.Uri === formValue.value.Uri);
                return matchesSearch && notInResults;
            });

        const matchingMissingSelectedOptions: FormSelectOption<ThesaurusConceptReference>[] =
            matchedMissingSelectedValues.map(formValue => ({
                label: this.thesaurusService.getThesaurusConceptReferenceLabel(formValue.value),
                value: formValue.value
            }));

        const searchResultOptions: FormSelectOption<ThesaurusConceptReference>[] = searchResultConcepts.map(
            concept => ({
                label: this.thesaurusService.getThesaurusConceptReferenceLabel(concept),
                value: concept
            })
        );

        return {
            options: [...matchingMissingSelectedOptions, ...searchResultOptions],
            isLiveData: true
        };
    }
    // #endregion

    // #region Submit suggestion
    private initNoResultsActionItem() {
        this.noResultsActionItem$ = this.search$.pipe(map(searchTerm => this.getNoResultsActionItem(searchTerm)));
    }

    private getNoResultsActionItem(searchTerm: string): ActionItem | null {
        if (!this.enableSuggestions || !searchTerm || searchTerm.length === 0) {
            return null;
        }

        return {
            label: this.translateService.translate('thesaurus.thesaurus-tree-dialog.add-suggestion'),
            style: ActionItemStyle.Link,
            eventListener: this.onThesaurusSuggestionSubmit.bind(this, searchTerm),
            disabled: this.suggestionLimitReached,
            disabledInfoMsg: this.translateService.translate('thesaurus.thesaurus-tree-dialog.suggestion-limit', {
                max: 1
            })
        };
    }
    // #endregion

    // #region Dropdown interactions
    public openDropdown(): void {
        this.openDropdown$.next(true);
    }

    public closeDropdown(): void {
        this.openDropdown$.next(false);
    }

    public toggleDropdown(): void {
        this.openDropdown$.next(!this.openDropdown$.value);
    }
    // #endregion

    // #region Interactions
    public onThesaurusSuggestionSubmit(term: string): void {
        this.thesaurusSearchControl.setValue([]);
        this.suggestion.next(term);
    }

    public onSearchTermChange(term: string): void {
        this.search$.next(term);
    }

    public onSelectionStateChange(selection: FormValue<FormSelectOption<ThesaurusConceptReference>>[]) {
        this.state$.next(selection);
    }
    // #endregion
}
